import { pushToMatomo } from './matomo-analytics';
import { getPlatform } from './utils';

export interface CardClickObject {
  content_type: string;
  card_content: string;
}

export enum Source {
  direct = 'direct',
  recommended = 'recommended',
  AR = 'AR',
  SPARKS = 'SPARKS',
  WEEKLY_GIVEAWAY = 'WEEKLY_GIVEAWAY',
  MINI_GAME = 'MINI_GAME',
}

export interface EcommerceObject {
  ecommerce: {
    value: number;
    total?: number; // This is matomo compatible value field
    currency: 'USD';
    items: EcommerceItem[];
    purchase?: EcommercePurchase;
  };
  product_source: Source;
}

export interface EcommercePurchase {
  id: string;
  revenue: number;
}

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  affiliation: string;
  coupon: string;
  discount: number;
  index: number;
  item_brand: string;
  item_category: string | string[] | null;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
  item_source?: string;
  added_in_store?: string;
  item_list_id: string;
  item_list_name: string;
  item_variant: string;
  location_id: string;
  price: number;
  item_price?: number; // This is matomo compatible price field
  quantity: number;
}

export interface CartItemProps {
  id: string;
  name: string;
  imageSrc: string;
  color: string;
  size: string;
  diameter: string;
  shape: string;
  item_source?: string;
  added_in_store?: string;
  price: number;
  quantity: number;
  onItemRemove?: () => void;
}

export interface LevelEventObject {
  name: string;
  timeInteractedInSeconds: number;
  type: string;
}

export interface VideoEventObject {
  title: string;
  url: string;
  type: string;
  timeInteractedInSeconds: number;
}

export interface CommonProps {
  active: boolean;
  close: () => void;
}

export enum EventAction {
  SHARE = 'SHARE',
  CLICK = 'CLICK',
  VIEW = 'VIEW',
  ADD = 'ADD',
  START = 'START',
  END = 'END',
  POST = 'POST',
  COLLECT = 'COLLECT',
  ENTER = 'ENTER',
  ENABLE = 'ENABLE',
  DISABLE = 'DISABLE',
  PURCHASE = 'PURCHASE',
}

export enum EventCategory {
  PRODUCT = 'PRODUCT',
  CART = 'CART',
  MINIGAME = 'MINIGAME',
  VIDEO = 'VIDEO',
  GLOBAL = 'GLOBAL',
  COUPON = 'COUPON',
  PRODUCT_AR = 'PRODUCT_AR',
  ECOMMERCE = 'ECOMMERCE',
}

export enum EventName {
  CART = 'CART',
  SPARKLE = 'SPARKLE',
  SWEEPSTAKES = 'SWEEPSTAKES',
  AUDIO = 'AUDIO',
  COPY_COUPON = 'COPY_COUPON',
  SCORE = 'SCORE',
}

export enum MetaPixelAction {
  VIEW_CONTENT = 'ViewContent',
  ADD_TO_CART = 'AddToCart',
  PURCHASE = 'Purchase',
  ADD_TO_WISHLIST = 'AddToWishlist',
}

export enum TiktokPixelAction {
  VIEW_CONTENT = 'ViewContent',
  ADD_TO_CART = 'AddToCart',
  INITIATE_CHECKOUT = 'InitiateCheckout',
  ADD_TO_WISHLIST = 'AddToWishlist',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _paq: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fbq: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _mtm: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ttq: any;
  }
}

type ChangeShopEvent = {
  change_shop_target: string;
  change_shop_medium: string;
  store?: string;
};

export function initAnalytics() {
  console.log('initialising analytics ----- walmart-app');
  window.dataLayer = window.dataLayer || [];
  const trackingSource = getPlatform();
  window.dataLayer.push({ tracking_source: trackingSource });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function pushToDataLayer(object: any) {
  if (window.parent == undefined) {
    console.warn('Parent could not be found.');
    return;
  }
  if (window.parent.dataLayer == undefined) {
    console.warn('Data layer on parent could not be found.');
    return;
  }
  window.parent.dataLayer.push(object);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function pushToAnalytics(object: any, totalCartValue?: number) {
  pushToDataLayer(object);
  pushToMatomo(object, totalCartValue);
}

export const trackMetaPixel = (action: MetaPixelAction, totalValue?: number, currency?: string) => {
  if (window.parent == undefined) {
    console.warn('Parent could not be found.');
    return;
  }
  if (window.parent.fbq == undefined) {
    console.warn('Meta track function not found');
    return;
  }
  if (action == MetaPixelAction.PURCHASE) {
    window.parent.fbq('track', action.toString(), { value: totalValue, currency: currency });
  }
  window.parent.fbq('track', action.toString());
};

export const trackTiktokPixel = (
  action: TiktokPixelAction,
  contentType?: string,
  description?: string,
  contentIds?: string[],
  value?: number,
  currency?: string,
) => {
  if (window.parent == undefined) {
    console.warn('Parent could not be found.');
    return;
  }
  if (window.parent.ttq == undefined) {
    console.warn('Tiktok track function not found');
    return;
  }

  const params = {
    content_type: contentType ?? '',
    value: value ?? 0,
    content_ids: contentIds,
    currency: currency ?? 'USD',
    description: description,
  };
  window.parent.ttq.track(action, params);
};

export const dispatchShareToAnalytics = (content_type: string, item_name: string, item_url: string) => {
  pushToAnalytics({
    event: 'share',
    content_type: content_type,
    item_name: item_name,
    item_url: item_url,
  });
};

export const dispatchChangeShopToAnalytics = (changeRoomEvent: ChangeShopEvent) => {
  pushToAnalytics({
    event: 'change_shop',
    ...changeRoomEvent,
  });
};

export const dispatchViewCartToAnalytics = (ecommerceObject: EcommerceObject) => {
  if (window.parent && window.parent.dataLayer) {
    window.parent.dataLayer.push({ ecommerce: null });
  }
  pushToAnalytics({
    event: 'view_cart',
    ecommerceObjectStr: JSON.stringify(ecommerceObject),
    ...ecommerceObject,
  });
  trackMetaPixel(MetaPixelAction.VIEW_CONTENT);
  trackTiktokPixel(TiktokPixelAction.VIEW_CONTENT, 'product_group', 'cart', ['1']);
};

export const dispatchRemoveFromCartToAnalytics = (totalCartValue: number, productSKU: string) => {
  pushToAnalytics(
    {
      event: 'remove_from_cart',
      ecommerce: {
        product_sku: productSKU,
      },
    },
    totalCartValue,
  );
};

export const dispatchUpdateCartQuantityToAnalytics = (productSKU: string, totalCartValue: number) => {
  const ecommerceObject: EcommerceObject = retrieveEcommerceObjectFromWMCart();
  const items: EcommerceItem[] = ecommerceObject.ecommerce.items;
  if (items) {
    const updatedItem: EcommerceItem | undefined = items.find((item) => item.item_id === productSKU);
    if (updatedItem) {
      const tempTotalCartValue = totalCartValue - updatedItem.price * updatedItem.quantity;
      dispatchRemoveFromCartToAnalytics(tempTotalCartValue, updatedItem.item_id);
      dispatchAddToCartToMatomo(ecommerceObject, totalCartValue); // for matomo with all cart items
      const updatedEcommerceObject = JSON.parse(JSON.stringify(ecommerceObject));
      updatedEcommerceObject.ecommerce.items = [updatedItem];
      dispatchAddToCartToAnalytics(updatedEcommerceObject); // for GCP with only updated item
    }
  }
};

// This event contains whole cart every time
export const dispatchAddToCartToMatomo = (ecommerceObject: EcommerceObject, totalCartValue: number) => {
  pushToMatomo(
    {
      event: 'add_to_cart',
      ...ecommerceObject,
    },
    totalCartValue,
  );
};

// This event contains only added item every time. // For GCP
export const dispatchAddToCartToAnalytics = (ecommerceObject: EcommerceObject) => {
  if (window.parent.dataLayer !== undefined) {
    window.parent.dataLayer.push({ ecommerce: null });
  }
  pushToDataLayer({
    event: 'add_to_cart',
    ecommerceObjectStr: JSON.stringify(ecommerceObject),
    ...ecommerceObject,
  });
  trackMetaPixel(MetaPixelAction.ADD_TO_CART);
  trackTiktokPixel(
    TiktokPixelAction.ADD_TO_CART,
    'product',
    ecommerceObject.product_source,
    ecommerceObject.ecommerce.items.map((item) => item.item_id),
    ecommerceObject.ecommerce.value,
    ecommerceObject.ecommerce.currency,
  );
};

export const dispatchViewItemtoAnalytics = (ecommerceObject: EcommerceObject) => {
  if (window.parent && window.parent.dataLayer) {
    window.parent.dataLayer.push({ ecommerce: null });
  }

  pushToAnalytics({
    event: 'view_item',
    item_name: ecommerceObject?.ecommerce?.items[0]?.item_name,
    ecommerceObjectStr: JSON.stringify(ecommerceObject),
    ...ecommerceObject,
  });
  trackMetaPixel(MetaPixelAction.VIEW_CONTENT);
  trackTiktokPixel(
    TiktokPixelAction.VIEW_CONTENT,
    'product_set',
    ecommerceObject.product_source,
    ecommerceObject.ecommerce.items.map((item) => item.item_id),
    ecommerceObject.ecommerce.value,
    ecommerceObject.ecommerce.currency,
  );
};

export const dispatchViewProductSettoAnalytics = (ecommerceObject: EcommerceObject, item_list_name: string) => {
  if (window.parent && window.parent.dataLayer) {
    window.parent.dataLayer.push({ ecommerce: null });
  }

  pushToAnalytics({
    event: 'view_product_set',
    ecommerceObjectStr: JSON.stringify(ecommerceObject),
    ...ecommerceObject,
  });
  pushToAnalytics({
    event: 'view_item_list',
    ecommerce: {
      item_list_name: item_list_name,
    },
  });
  trackMetaPixel(MetaPixelAction.VIEW_CONTENT);
  trackTiktokPixel(
    TiktokPixelAction.VIEW_CONTENT,
    'product_set',
    ecommerceObject.product_source,
    ecommerceObject.ecommerce.items.map((item) => item.item_id),
    ecommerceObject.ecommerce.value,
    ecommerceObject.ecommerce.currency,
  );
};

export const dispatchCardClickToAnalytics = (itemObject: CardClickObject) => {
  const obj = {
    ...itemObject,
    event: 'card_click',
  };
  pushToAnalytics(obj);
};

export const dispatchTurnTableInteractionToAnalytics = (ecommerceObject: EcommerceObject) => {
  if (window.parent && window.parent.dataLayer) {
    window.parent.dataLayer.push({ ecommerce: null });
  }
  pushToAnalytics({
    event: 'turntable_interaction',
    ecommerceObjectStr: JSON.stringify(ecommerceObject),
    ...ecommerceObject,
  });
};
// Needs to be called the sparks are collected.
export const dispatchEarnVirtualCurrencyAnalytics = () => {
  pushToAnalytics({
    event: 'earn_virtual_currency',
  });
};

export const dispatchSweepstakesEntry = (sweepstakes_entry: string, source?: Source) => {
  pushToAnalytics({
    event: 'enter_sweepstakes',
    store: sweepstakes_entry,
    source: source ?? Source.SPARKS,
  });
  trackMetaPixel(MetaPixelAction.VIEW_CONTENT);
  trackTiktokPixel(TiktokPixelAction.VIEW_CONTENT, 'product_set', 'sweepstakes', ['1']);
};

// Needs to be called when audio is enabled or disabled.
export const dispatchEnableAudioAnalytics = (audioEnabled: boolean) => {
  pushToAnalytics({
    event: 'enable_audio',
    audio_enabled: audioEnabled,
  });
};

export const dispatchCopyCodeAnalytics = () => {
  pushToAnalytics({
    event: 'copy_code',
  });
};

export const dispatchViewARAnalytics = (product_id: string) => {
  pushToAnalytics({
    event: 'view_ar',
    item_name: product_id,
  });
  trackMetaPixel(MetaPixelAction.VIEW_CONTENT);
  trackTiktokPixel(TiktokPixelAction.VIEW_CONTENT, 'AR');
};

// Needs to be called when spark collection starts
export const dispatchLevelStartAnalytics = (levelEvent: LevelEventObject) => {
  pushToAnalytics({
    event: 'level_start',
    level_name: levelEvent.name,
    content_type: levelEvent.type,
  });
};

// Needs to be called when a minigame or spark collection is finished.
export const dispatchLevelEndAnalytics = (levelEvent: LevelEventObject) => {
  pushToAnalytics({
    event: 'level_end',
    level_name: levelEvent.name,
    content_type: levelEvent.type,
    time_interacting_in_seconds: levelEvent.timeInteractedInSeconds,
  });
};

// Needs to be called when a video starts.
export const dispatchVideoStartAnalytics = (videoEvent: VideoEventObject) => {
  pushToAnalytics({
    event: 'video_start',
    video_title: videoEvent.title,
    video_url: videoEvent.url,
    content_type: videoEvent.type,
  });
};

// Needs to be called when a video ends.
export const dispatchVideoEndAnalytics = (videoEvent: VideoEventObject) => {
  pushToAnalytics({
    event: 'video_end',
    video_title: videoEvent.title,
    video_url: videoEvent.url,
    content_type: videoEvent.type,
    time_interacting_in_seconds: videoEvent.timeInteractedInSeconds,
  });
};

// Needs to be called when a minigame is finished.
export const dispatchPostScoreAnalytics = (score: number) => {
  pushToAnalytics({
    event: 'post_score',
    score: score,
  });
};

export const dispatchPurchaseToAnalytics = (ecommerceObject: EcommerceObject) => {
  if (window?.parent && window?.parent?.dataLayer) {
    window.parent.dataLayer.push({ ecommerce: null });
  }
  ecommerceObject.ecommerce.purchase = {
    id: Math.random().toString(36).substring(2, 15),
    revenue: ecommerceObject.ecommerce.value,
  };
  pushToAnalytics({
    event: 'purchase',
    ecommerceObjectStr: JSON.stringify(ecommerceObject),
    ...ecommerceObject,
  });
  trackMetaPixel(MetaPixelAction.PURCHASE, ecommerceObject.ecommerce.value, ecommerceObject.ecommerce.currency);
  trackTiktokPixel(
    TiktokPixelAction.INITIATE_CHECKOUT,
    'product_group',
    ecommerceObject.product_source,
    ecommerceObject.ecommerce.items.map((item) => item.item_id),
    ecommerceObject.ecommerce.value,
    ecommerceObject.ecommerce.currency,
  );
};

export const retrieveEcommerceObjectFromWMCart = (): EcommerceObject => {
  const cart = localStorage.getItem('app_wm_cart');
  let ecommerceObject: EcommerceObject = {
    ecommerce: { value: 0, currency: 'USD', items: [] },
    product_source: Source.direct,
  };
  if (cart == null) return ecommerceObject;
  const jsonCart = JSON.parse(cart);
  const ecommerceItems: EcommerceItem[] = [];
  let price = 0;
  jsonCart.map((rec: CartItemProps) => {
    ecommerceItems.push({
      item_id: rec.id,
      item_name: rec.name,
      affiliation: '',
      coupon: '',
      discount: 0,
      index: 0,
      item_brand: '',
      item_category: null,
      item_category2: '',
      item_category3: '',
      item_category4: '',
      item_category5: '',
      item_list_id: '',
      item_list_name: '',
      item_variant: '',
      location_id: '',
      item_source: rec.item_source,
      added_in_store: rec.added_in_store,
      price: rec.price,
      quantity: rec.quantity,
    });
    price += rec.price * rec.quantity;
  });
  ecommerceObject = {
    ecommerce: { value: price, currency: 'USD', items: ecommerceItems },
    product_source: Source.direct,
  };
  return ecommerceObject;
};

export const dispatchSelectedMusic = (music: string) => {
  pushToAnalytics({
    event: 'select_music',
    music_name: music,
  });
};

export const dispatchSelectedWalmartPlus = () => {
  pushToAnalytics({
    event: 'select_walmart_plus',
  });
};

export const dispatchShareWishlist = (ecommerceObject: EcommerceObject) => {
  pushToAnalytics({
    event: 'share_wishlist',
    ecommerceObjectStr: JSON.stringify(ecommerceObject),
    ...ecommerceObject,
  });
};

export const dispatchAddToWishlist = (ecommerceObject: EcommerceObject) => {
  pushToAnalytics({
    event: 'add_to_wishlist',
    source: ecommerceObject.product_source,
    item_name: ecommerceObject?.ecommerce?.items[0]?.item_name,
    ecommerceObjectStr: JSON.stringify(ecommerceObject),
    ...ecommerceObject,
  });
  trackMetaPixel(MetaPixelAction.ADD_TO_WISHLIST);
  trackTiktokPixel(
    TiktokPixelAction.ADD_TO_WISHLIST,
    'product',
    ecommerceObject.product_source,
    ecommerceObject.ecommerce.items.map((item) => item.item_id),
    ecommerceObject.ecommerce.value,
    ecommerceObject.ecommerce.currency,
  );
};

export const dispatchViewWishlist = (ecommerceObject: EcommerceObject) => {
  pushToAnalytics({
    event: 'view_wishlist',
    ecommerceObjectStr: JSON.stringify(ecommerceObject),
    ...ecommerceObject,
  });
  trackMetaPixel(MetaPixelAction.VIEW_CONTENT);
  trackTiktokPixel(TiktokPixelAction.VIEW_CONTENT, 'wishlist');
};
